/**
 * Feedback Hook
 *
 * Manages feedback state and API interactions for the query feedback system.
 */

import { useState, useCallback } from 'react';
import { apiRequest } from '../services/api';

/**
 * Hook for managing feedback operations
 */
export function useFeedback() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  /**
   * Create new feedback for a message
   */
  const createFeedback = useCallback(async (messageId, sessionId, issueType, comment) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest('/api/v1/feedback', {
        method: 'POST',
        body: JSON.stringify({
          message_id: messageId,
          session_id: sessionId,
          issue_type: issueType,
          comment: comment
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Handle FastAPI validation errors (array of objects) vs simple error strings
        let errorMessage = 'Failed to submit feedback';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // FastAPI validation error format: [{loc: [...], msg: "...", type: "..."}]
            errorMessage = errorData.detail.map(err => err.msg).join(', ');
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get list of feedback with filters
   */
  const getFeedbackList = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.issueType) params.append('issue_type', filters.issueType);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const response = await apiRequest(`/api/v1/feedback?${params.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch feedback list');
      }

      const result = await response.json();
      setFeedbackList(result.feedback || []);
      setPagination({
        total: result.total,
        limit: result.limit,
        offset: result.offset,
        hasMore: result.has_more
      });

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get feedback statistics
   */
  const getFeedbackStats = useCallback(async () => {
    try {
      const response = await apiRequest('/api/v1/feedback/stats');

      if (!response.ok) {
        throw new Error('Failed to fetch feedback stats');
      }

      const result = await response.json();
      setFeedbackStats(result);
      return result;
    } catch (err) {
      console.error('Failed to fetch feedback stats:', err);
      return null;
    }
  }, []);

  /**
   * Get detailed feedback by ID
   */
  const getFeedbackDetail = useCallback(async (feedbackId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/v1/feedback/${feedbackId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch feedback detail');
      }

      const result = await response.json();
      // API returns { success: true, feedback: {...} } - extract the feedback object
      const feedbackData = result.feedback || result;
      setSelectedFeedback(feedbackData);
      return feedbackData;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update feedback status (admin only)
   */
  const updateFeedback = useCallback(async (feedbackId, status, resolutionNotes = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/v1/feedback/${feedbackId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: status,
          resolution_notes: resolutionNotes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update feedback');
      }

      const result = await response.json();

      // Update the local list if the updated item is in it
      setFeedbackList(prev => prev.map(item =>
        item.id === feedbackId ? { ...item, ...result } : item
      ));

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get feedback for a specific message
   */
  const getMessageFeedback = useCallback(async (messageId) => {
    try {
      const response = await apiRequest(`/api/v1/feedback/message/${messageId}`);

      if (!response.ok) {
        return [];
      }

      return await response.json();
    } catch (err) {
      console.error('Failed to fetch message feedback:', err);
      return [];
    }
  }, []);

  /**
   * Delete feedback
   */
  const deleteFeedback = useCallback(async (feedbackId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiRequest(`/api/v1/feedback/${feedbackId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete feedback');
      }

      // Remove from local list
      setFeedbackList(prev => prev.filter(item => item.id !== feedbackId));

      return true;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    feedbackList,
    feedbackStats,
    selectedFeedback,
    pagination,

    // Actions
    createFeedback,
    getFeedbackList,
    getFeedbackStats,
    getFeedbackDetail,
    updateFeedback,
    getMessageFeedback,
    deleteFeedback,
    setSelectedFeedback,
    setError
  };
}

export default useFeedback;
